import {
  shipmentSchemaInternational,
  shipmentSchemaDomestic,
  shipmentSchemaSea,
  shipmentSchemaAir,
} from "../schemavalidations/shipmentschema";

/**
 * Runtime check: does this shipment have all the data required to produce a
 * valid CMR (International) or Delivery Note (Domestic)?
 *
 * Uses the same Yup schemas as the footer "Validate CMR / Validate Delivery
 * Note" action so the preview, table, sharing modal and driver view stay in
 * sync with the form-level validation.
 *
 * Replaces the legacy `shipment.documentRequire` boolean, which was driven by
 * a user-toggled checkbox rather than the actual shape of the data.
 */

const getCountryId = (country: any): string => {
  if (!country) return "";
  if (typeof country === "object" && country !== null && "_id" in country) {
    return country._id as string;
  }
  return country as string;
};

const normalizeAddress = (address: any) =>
  address
    ? {
        ...address,
        country: getCountryId(address?.country),
      }
    : address;

const normalizeParty = (party: any) =>
  party
    ? {
        ...party,
        address: normalizeAddress(party.address),
      }
    : party;

const normalizeShipment = (shipment: any) => {
  if (!shipment) return shipment;
  return {
    ...shipment,
    pickup: normalizeParty(shipment.pickup),
    delivery: normalizeParty(shipment.delivery),
    carrier: normalizeParty(shipment.carrier),
    client: normalizeParty(shipment.client),
  };
};

const schemaForShipment = (shipment: any) =>
  shipment?.type === "Domestic"
    ? shipmentSchemaDomestic
    : shipmentSchemaInternational;

/**
 * Cheap pass/fail check. Use this for UI gates (table row actions, sharing
 * modal file lists, driver view document card visibility).
 */
export const isShipmentDocumentReady = (shipment: any): boolean => {
  if (!shipment) return false;
  try {
    return schemaForShipment(shipment).isValidSync(normalizeShipment(shipment), {
      abortEarly: true,
      context: { requireRegistrationCode: true },
    });
  } catch {
    return false;
  }
};

/**
 * Sea equivalent of {@link isShipmentDocumentReady}: does this shipment have
 * everything needed to produce a Bill of Lading (BOL)? Validates against the
 * sea schema (same one the "Validate BOL" button runs), so the BOL preview
 * action only appears once sea validation would pass. Returns false for any
 * non-SEA shipment.
 */
export const isShipmentBolReady = (shipment: any): boolean => {
  if (!shipment) return false;
  if ((shipment.transportMode ?? "ROAD") !== "SEA") return false;
  try {
    return shipmentSchemaSea.isValidSync(normalizeShipment(shipment), {
      abortEarly: true,
      context: { requireRegistrationCode: true },
    });
  } catch {
    return false;
  }
};

/**
 * Air equivalent of {@link isShipmentBolReady}: does this shipment have
 * everything needed to produce an Air Waybill (AWB)? Validates against the air
 * schema (same one the "Validate AWB" button runs). Returns false for any
 * non-AIR shipment.
 */
export const isShipmentAwbReady = (shipment: any): boolean => {
  if (!shipment) return false;
  if ((shipment.transportMode ?? "ROAD") !== "AIR") return false;
  try {
    return shipmentSchemaAir.isValidSync(normalizeShipment(shipment), {
      abortEarly: true,
      context: { requireRegistrationCode: true },
    });
  } catch {
    return false;
  }
};

/**
 * Full check that also returns the list of missing-field error messages.
 * Use this when you need to explain to the user *why* a CMR / Delivery Note
 * isn't available yet (e.g. tooltip, inline hint, future status badge).
 */
export const getShipmentDocumentStatus = (
  shipment: any,
): { ready: boolean; missing: string[] } => {
  if (!shipment) return { ready: false, missing: [] };
  try {
    schemaForShipment(shipment).validateSync(normalizeShipment(shipment), {
      abortEarly: false,
      context: { requireRegistrationCode: true },
    });
    return { ready: true, missing: [] };
  } catch (err: any) {
    const missing: string[] = Array.isArray(err?.inner)
      ? err.inner.map((e: any) => e?.message).filter(Boolean)
      : err?.message
        ? [err.message]
        : [];
    return { ready: false, missing };
  }
};
